import React from "react";
import { Eraser, HardDriveDownload, Cpu, Stethoscope, Tag, ArrowRight } from "lucide-react";

const STEPS = [
  { icon: Eraser, title: "Erase", body: "Every drive sanitized and verified to standard." },
  { icon: HardDriveDownload, title: "Reinstall", body: "A clean, genuine OS laid back down automatically." },
  { icon: Cpu, title: "Drivers", body: "The right drivers detected and applied for the exact machine." },
  { icon: Stethoscope, title: "Assessment", body: "Full hardware diagnostics captured into the device record." },
  { icon: Tag, title: "Certify & label", body: "A signed certificate and a printed label, tied to the device." },
];

const FullCycle = () => {
  return (
    <section id="cycle" className="relative bg-white font-body">
      <div className="mx-auto max-w-7xl px-6 py-20 lg:py-28">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-line bg-brand-bg px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-brand-plum">
            More than erasure
          </span>
          <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-brand-plum sm:text-4xl lg:text-5xl">
            One Pass Down the Bench, <span className="text-brand-gradient">Ready to Resell</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-ink sm:text-lg">
            Poofed isn&apos;t a standalone wipe tool you bolt onto your process. It&apos;s one stage of
            Furloop&apos;s end-to-end refurbishment cycle — all on the same station, the same device record.
          </p>
        </div>

        {/* pipeline */}
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {STEPS.map(({ icon: Icon, title, body }, i) => (
            <div key={title} className="relative">
              <div className="flex h-full flex-col rounded-2xl border border-line bg-white p-5 transition-shadow hover:shadow-xl hover:shadow-brand-plum/5">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand-wash text-brand-magenta ring-1 ring-brand-orchid/30">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="font-display text-xs font-semibold uppercase tracking-wide text-brand-orchid">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-3 font-display text-base font-semibold text-brand-plum">{title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted">{body}</p>
              </div>
              {i < STEPS.length - 1 && (
                <ArrowRight className="absolute -right-3 top-1/2 z-10 hidden h-5 w-5 -translate-y-1/2 text-brand-orchid lg:block" />
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-4 rounded-3xl border border-brand-orchid/30 bg-gradient-to-r from-brand-wash to-white p-6 sm:flex-row sm:items-center sm:p-8">
          <p className="max-w-2xl font-display text-lg font-medium text-brand-plum sm:text-xl">
            From <span className="text-brand-magenta">&ldquo;previous owner&apos;s data&rdquo;</span> to{" "}
            <span className="text-brand-magenta">&ldquo;graded, reinstalled, certified, ready to resell&rdquo;</span>{" "}
            — no separate tools, no re-handling, one record per device.
          </p>
          <a
            href="/"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-line bg-white px-5 py-3 text-sm font-semibold text-brand-plum no-underline transition-colors hover:border-brand-orchid hover:text-brand-magenta hover:no-underline"
          >
            Explore the full platform
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default FullCycle;
